import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Mail, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '@/api/client';
import { requireAnonymous } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PublicShell } from '@/components/PublicShell';

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: ({ context }) => requireAnonymous({ context }),
  component: ForgotPasswordPage,
});

const schema = z.object({ email: z.string().email('Valid email required') });
type Values = z.infer<typeof schema>;

function ForgotPasswordPage() {
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '' } });
  const send = useMutation({
    mutationFn: (v: Values) => api('/auth/forgot-password', { method: 'POST', json: v }),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-6 py-16">
        <div className="section-ref">Password reset</div>
        <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight">
          Reset your password
        </h1>
        <div className="mt-3 gov-rule" aria-hidden />
        <p className="mt-6 text-[15px] leading-relaxed text-(--color-ink-2)">
          Enter the email on your account and we'll send a link to set a new password.
        </p>

        <Card className="mt-8">
          <CardContent className="p-8">
            {send.isSuccess ? (
              <div className="flex items-start gap-3 rounded-(--radius-sm) border-l-[3px] border-(--color-forest) bg-(--color-forest-soft) px-4 py-4">
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-(--color-forest)" />
                <div className="text-[14px] leading-relaxed text-(--color-ink-2)">
                  <strong className="text-(--color-forest)">Sent.</strong> If an account exists
                  for that email, a reset link is on its way. Check your inbox (and the dev logs
                  in non-prod environments).
                </div>
              </div>
            ) : (
              <form onSubmit={form.handleSubmit((v) => send.mutate(v))} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="fp-email" required>
                    Email address
                  </Label>
                  <Input
                    id="fp-email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={!!form.formState.errors.email || undefined}
                    {...form.register('email')}
                  />
                  {form.formState.errors.email && (
                    <p className="flex items-center gap-1.5 text-[12px] font-medium text-(--color-maroon)">
                      <AlertTriangle className="size-3.5" />
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={send.isPending}>
                  <Mail className="size-4" />
                  {send.isPending ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-[14px] text-(--color-ink-2)">
          <Link to="/login" className="gov-link font-medium">
            Back to sign in
          </Link>
        </p>
      </div>
    </PublicShell>
  );
}
